<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import MultiselectDropdown from '../../../../../shared/components/ui/MultiselectDropdown.vue';

const props = defineProps({
  teams: {
    type: Array,
    default: () => [],
  },
  selectedTeamId: {
    type: [Number, String],
    default: null,
  },
  updateHistory: {
    type: Boolean,
    default: false,
  },
  ticketId: {
    type: [Number, String],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:selectedTeamId', 'change']);

const store = useStore();

const selectedTeam = computed(() => {
  return props.teams.find(team => team.id === props.selectedTeamId) || {};
});

const handleTeamChange = async newTeam => {
  if (props.disabled) return;

  if (newTeam.id === props.selectedTeamId) {
    // If the same team is selected, remove it
    emit('update:selectedTeamId', null);
    emit('change', null);

    // If updateHistory is true, also call the remove function
    if (props.updateHistory && props.ticketId) {
      await handleRemoveTeam();
    }
    return;
  }

  emit('update:selectedTeamId', newTeam.id);
  emit('change', newTeam);

  // If updateHistory is true, update the ticket history
  if (props.updateHistory && props.ticketId) {
    try {
      const response = await store.dispatch('tickets/changeTeam', {
        id: props.ticketId,
        newTeam: newTeam.id,
      });

      if (response && response.team_name) {
        store.commit('tickets/UPDATE_TICKET_TEAM', {
          id: props.ticketId,
          team_name: response.team_name,
          team_id: response.team_id,
        });
      }

      // Reload ticket to ensure status label is updated after automatic status change
      await store.dispatch('tickets/getTicket', props.ticketId);
      // Refresh the ticket history immediately after the change
      await store.dispatch('tickets/getTicketHistory', props.ticketId);
    } catch (error) {
      console.error('Error updating team:', error);
      // Revert the change on error
      emit('update:selectedTeamId', props.selectedTeamId);
    }
  }
};

const handleRemoveTeam = async () => {
  if (!props.updateHistory || !props.ticketId) return;

  try {
    const response = await store.dispatch('tickets/removeTeam', {
      id: props.ticketId,
    });

    if (response) {
      emit('update:selectedTeamId', null);
      emit('change', null);
    }

    // Reload ticket to ensure status label is updated after automatic status change
    await store.dispatch('tickets/getTicket', props.ticketId);
    // Refresh the ticket history immediately after the change
    await store.dispatch('tickets/getTicketHistory', props.ticketId);
  } catch (error) {
    console.error('Error removing team:', error);
  }
};
</script>

<template>
  <div>
    <div @click.prevent>
      <MultiselectDropdown
        :options="teams"
        :selected-item="selectedTeam"
        multiselector-title="Selecionar Time"
        multiselector-placeholder="Selecionar Time"
        input-placeholder="Buscar time..."
        no-search-result="Nenhum time encontrado"
        :has-thumbnail="false"
        :disabled="disabled"
        @select="handleTeamChange"
      />
    </div>
  </div>
</template>
